import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido a VerdantPOS");
      navigate("/");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1637311252429-634d760e08b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')" }}
        />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
            <Leaf className="h-6 w-6 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-2xl">VerdantPOS</div>
            <div className="text-xs text-primary-foreground/70">by LabAroid</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight">Operación comercial para viveros, en un solo sistema.</h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Ventas, inventario, órdenes, cobros, operaciones, comprobantes y analítica conectados al flujo operativo real.
          </p>
        </div>
        <p className="relative text-sm text-primary-foreground/60">© 2026 LabAroid · VerdantPOS</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="lg:hidden flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-xl text-primary">VerdantPOS</div>
              <div className="text-[10px] text-muted-foreground">by LabAroid</div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-foreground">Iniciar sesión</h2>
            <p className="text-sm text-muted-foreground mt-1">Ingresa tus credenciales para continuar.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="usuario@empresa.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Contraseña</Label>
            <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
          </div>

          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Ingresar
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Acceso restringido a personal autorizado.
          </p>
        </form>
      </div>
    </div>
  );
}
